import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Car, Mail, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ForgotPasswordPage = () => {
  const { i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      if (response.ok) {
        setSent(true);
        toast.success(i18n.language === 'es' 
          ? 'Email enviado correctamente' 
          : 'Email sent successfully');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Error al enviar el email');
      }
    } catch (error) {
      toast.error(i18n.language === 'es' 
        ? 'Error de conexión' 
        : 'Connection error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-12 h-12 bg-primary rounded-sm flex items-center justify-center">
            <Car className="w-7 h-7 text-black" />
          </div>
          <span className="font-heading font-bold text-2xl text-white">PLEYADES</span>
        </Link>

        {/* Card */}
        <div className="card-dark p-8">
          {sent ? (
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h1 className="font-heading text-2xl font-bold text-white">
                {i18n.language === 'es' ? 'Email enviado' : 'Email sent'}
              </h1>
              <p className="text-zinc-400 text-sm">
                {i18n.language === 'es' 
                  ? `Hemos enviado un enlace de recuperación a ${email}. Revisa tu bandeja de entrada y spam.`
                  : `We have sent a recovery link to ${email}. Check your inbox and spam folder.`}
              </p>
              <Link to="/login">
                <Button variant="outline" className="mt-4">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {i18n.language === 'es' ? 'Volver al login' : 'Back to login'}
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-heading text-2xl font-bold text-white text-center mb-2">
                {i18n.language === 'es' ? 'Recuperar contraseña' : 'Reset password'}
              </h1>
              <p className="text-zinc-400 text-sm text-center mb-6">
                {i18n.language === 'es' 
                  ? 'Ingresa tu email y te enviaremos un enlace para restablecer tu contraseña.'
                  : 'Enter your email and we will send you a link to reset your password.'}
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-white">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="email@ejemplo.com"
                      className="input-gold pl-10"
                      required
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-primary"
                  data-testid="forgot-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {i18n.language === 'es' ? 'Enviando...' : 'Sending...'}
                    </>
                  ) : (
                    i18n.language === 'es' ? 'Enviar enlace' : 'Send link'
                  )}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <Link to="/login" className="text-zinc-400 text-sm hover:text-primary flex items-center justify-center gap-1">
                  <ArrowLeft className="w-4 h-4" />
                  {i18n.language === 'es' ? 'Volver al login' : 'Back to login'}
                </Link>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPasswordPage;
